import React from "react";
import { RankingPlayer } from "types/ranking";
import { getAvatarImage } from "utils/avatars";
import "./styles.scss";

// Importar imagem do pódio SVG e background
import podioSvg from "assets/img/podio.svg";
import bgMaquiagem from "assets/video/bg_maquiagem.jpg";

interface BannerPodiumMakeupProps {
  title?: string;
  players: RankingPlayer[];
  isLoading?: boolean;
}

export const BannerPodiumMakeup: React.FC<BannerPodiumMakeupProps> = ({
  title = "Top 3",
  players,
  isLoading = false,
}) => {
  // Garantir que temos exatamente 3 jogadores (preenchendo com vazios se necessário)
  const topThree = [...players];
  while (topThree.length < 3) {
    topThree.push({
      id: `empty-${topThree.length}`,
      name: "---",
      position: topThree.length + 1,
      points: 0,
    });
  }

  // Extrair posições: 1º, 2º, 3º
  const [first, second, third] = topThree.slice(0, 3);

  const getPlayerInitials = (name: string): string => {
    if (!name || name === "---") return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getMedalColor = (position: number): string => {
    switch (position) {
      case 1:
        return "#FFD700"; // Ouro
      case 2:
        return "#C0C0C0"; // Prata
      case 3:
        return "#CD7F32"; // Bronze
      default:
        return "#999999";
    }
  };

  if (isLoading) {
    return (
      <div className="makeup-podium-ranking">
        <div className="makeup-podium-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Carregando ranking...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="makeup-podium-ranking">
      {/* Background Image */}
      <div 
        className="makeup-background"
        style={{ backgroundImage: `url(${bgMaquiagem})` }}
      />

      <div className="makeup-podium-container">
        {/* SVG do Pódio (fundo) */}
        <div className="podium-svg-wrapper">
          <img src={podioSvg} alt="Pódio" className="podium-svg" />
        </div>

        {/* Grid dos players no pódio */}
        <div className="podium-players-grid">
          {/* 2º Lugar (Esquerda) */}
          <div className="podium-position podium-second">
            <div className="player-card">
              {/* Medal */}
              <div 
                className="medal-number"
                style={{ backgroundColor: getMedalColor(second.position) }}
              >
                {second.position}
              </div>

              {/* Avatar */}
              <div className="player-avatar-container">
                {getAvatarImage(second.fotoDePerfil || second.avatar) ? (
                  <img
                    src={getAvatarImage(second.fotoDePerfil || second.avatar) || ""}
                    alt={second.name}
                    className="player-avatar-img"
                  />
                ) : (
                  <div className="player-avatar-placeholder">
                    {getPlayerInitials(second.name)}
                  </div>
                )}
              </div>

              {/* Nome */}
              <h3 className="player-name">{second.name}</h3>

              {/* Pontos */}
              <p className="player-points">
                {second.points.toLocaleString("pt-BR")} pts
              </p>
            </div>
          </div>

          {/* 1º Lugar (Centro - Maior) */}
          <div className="podium-position podium-first">
            <div className="player-card first-place">
              {/* Medal */}
              <div 
                className="medal-number"
                style={{ backgroundColor: getMedalColor(first.position) }}
              >
                {first.position}
              </div>

              {/* Avatar */}
              <div className="player-avatar-container">
                {getAvatarImage(first.fotoDePerfil || first.avatar) ? (
                  <img
                    src={getAvatarImage(first.fotoDePerfil || first.avatar) || ""}
                    alt={first.name}
                    className="player-avatar-img"
                  />
                ) : (
                  <div className="player-avatar-placeholder">
                    {getPlayerInitials(first.name)}
                  </div>
                )}
              </div>

              {/* Nome */}
              <h3 className="player-name">{first.name}</h3>

              {/* Pontos */}
              <p className="player-points">
                {first.points.toLocaleString("pt-BR")} pts
              </p>
            </div>
          </div>

          {/* 3º Lugar (Direita) */}
          <div className="podium-position podium-third">
            <div className="player-card">
              {/* Medal */}
              <div 
                className="medal-number"
                style={{ backgroundColor: getMedalColor(third.position) }}
              >
                {third.position}
              </div>

              {/* Avatar */}
              <div className="player-avatar-container">
                {getAvatarImage(third.fotoDePerfil || third.avatar) ? (
                  <img
                    src={getAvatarImage(third.fotoDePerfil || third.avatar) || ""}
                    alt={third.name}
                    className="player-avatar-img"
                  />
                ) : (
                  <div className="player-avatar-placeholder">
                    {getPlayerInitials(third.name)}
                  </div>
                )}
              </div>

              {/* Nome */}
              <h3 className="player-name">{third.name}</h3>

              {/* Pontos */}
              <p className="player-points">
                {third.points.toLocaleString("pt-BR")} pts
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
